import UserApi from "@/api/users/users";
import {
  ClientsAPI,
  ClientsQuery,
  ContactsAPI,
  ContactsQuery,
  CUSTOM_FIELD_CATEGORIES,
  CustomField,
  CustomFieldsQuery,
  EVIDENCE_PRIORITIES,
  EvidenceProgressAPI,
  EvidenceProgressQuery,
  EvidenceStatusAPI,
  EvidenceStatusQuery,
  EvidenceTypesAPI,
  EvidenceTypesQuery,
  GetClientsResponse,
  GetContactsResponse,
  GetEvidenceProgressResponse,
  GetEvidenceStatusResponse,
  GetEvidenceTypesResponse,
  GetLocationsResponse,
  GetOfficesResponse,
  GetOrganizationsResponse,
  LocationsAPI,
  LocationsQuery,
  OfficesAPI,
  OfficesQuery,
  OrganizationsAPI,
  OrganizationsQuery,
  UsersAPI,
} from "@/Features";
import { BasicUser } from "@/types/Users/Users";
import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";
import { useMemo } from "react";

const resolveCustomFieldFilter = (field: CustomField) => {
  const filter: FilterDefinition = {
    label: field.field_name,
    dataField: `custom_field_${field.field_id}`,
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  };

  switch (field.editor_type) {
    case "textBox":
      filter.inputType = "text";
      filter.operators = [
        Operators.Equals,
        Operators.NotEquals,
        Operators.Contains,
        Operators.DoesNotContain,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      break;
    case "dropDown":
      filter.inputType = "multiselect";
      filter.operators = [
        Operators.In,
        Operators.NIn,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      filter.selectOptions =
        field.options?.map((option) => ({
          label: option,
          value: option,
          data: option,
        })) || [];
      break;
    case "dateBox":
      filter.inputType = "datetime";
      filter.resolution = "day";
      filter.operators = [
        Operators.Equals,
        Operators.NotEquals,
        Operators.GreaterThan,
        Operators.LessThan,
        Operators.GreaterThanOrEqual,
        Operators.LessThanOrEqual,
        Operators.Between,
        Operators.NotBetween,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      break;
    case "tagBox":
      filter.inputType = "multiselect";
      filter.operators = [
        Operators.In,
        Operators.NIn,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      filter.selectOptions =
        field.options?.map((option) => ({
          label: option,
          value: option,
          data: option,
        })) || [];
      break;
    default:
      break;
  }

  return filter;
};

export const useEvidenceFilterDefinitions = (options?: {
  case_id?: number;
}) => {
  const { case_id } = options || {};
  const { data: customFields, isLoading: isCustomFieldsLoading } =
    CustomFieldsQuery.useGetCustomFields({
      category: CUSTOM_FIELD_CATEGORIES.EVIDENCE,
    });

  const baseFilters: FilterDefinition[] = useMemo(() => {
    const filters: FilterDefinition[] = [
      {
        label: "UUID",
        pluralLabel: "UUIDs",
        dataField: "uuid",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Evidence Number",
        pluralLabel: "Evidence Numbers",
        dataField: "evidence_number",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Case Number",
        pluralLabel: "Case Numbers",
        dataField: "case_number",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Case Name",
        pluralLabel: "Case Names",
        dataField: "case_ref",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Type",
        pluralLabel: "Types",
        dataField: "item_type",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([...EvidenceTypesQuery.queryKeys.infiniteList()]),
          loadPage: async ({ search }) => {
            const page =
              (await EvidenceTypesAPI.get()) as GetEvidenceTypesResponse;

            return {
              items: page.map((evidenceType) => ({
                label: evidenceType.evi_type,
                value: evidenceType.evi_type,
                data: evidenceType,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Item Name",
        pluralLabel: "Item Names",
        dataField: "item_name",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Provider",
        pluralLabel: "Providers",
        dataField: "manufacturer",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Unique ID",
        pluralLabel: "Unique IDs",
        dataField: "serial_number",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Assigned User",
        pluralLabel: "Assigned Users",
        dataField: "assigned_user_id",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([...UsersAPI.queryKeys.infiniteList({ case_id })]),
          loadPage: async ({ search }) => {
            const page = (await UserApi.getUsers({
              case_id,
            })) as BasicUser[];

            return {
              items: page.map((user) => ({
                label: user.full_name,
                value: user.user_id.toString(),
                data: {
                  user_id: user.user_id,
                  first_name: user.first_name,
                  last_name: user.last_name,
                  full_name: user.full_name,
                  email: user.email,
                },
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Priority",
        pluralLabel: "Priorities",
        dataField: "priority_id",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        selectOptions: EVIDENCE_PRIORITIES.map((p) => ({
          label: p.priority_name,
          value: p.priority_id.toString(),
          data: p,
        })),
      },
      {
        label: "Status",
        pluralLabel: "Statuses",
        dataField: "status_id",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([...EvidenceStatusQuery.queryKeys.infinteList()]),
          loadPage: async ({ search }) => {
            const page =
              (await EvidenceStatusAPI.get()) as GetEvidenceStatusResponse;

            return {
              items: page.data.map((status) => ({
                label: status.status_name,
                value: status.status_id.toString(),
                data: status,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Progress",
        pluralLabel: "Progresses",
        dataField: "progress",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([...EvidenceProgressQuery.queryKeys.infiniteList()]),
          loadPage: async ({ search }) => {
            const page =
              (await EvidenceProgressAPI.get()) as GetEvidenceProgressResponse;

            return {
              items: page.map((progress) => ({
                label: progress.progress_name,
                value: progress.progress_name.toString(),
                data: progress,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Created On",
        dataField: "created_on",
        inputType: "datetime",
        resolution: "millisecond",
        showTimeZone: "local",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "Intake Date",
        dataField: "intake_date",
        inputType: "datetime",
        resolution: "day",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Last Audit Date",
        dataField: "last_audit_date",
        inputType: "datetime",
        resolution: "day",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Location Received",
        pluralLabel: "Location Received",
        dataField: "location_received",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Location",
        pluralLabel: "Locations",
        dataField: "location_id",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(
              LocationsQuery.queryKeys.infiniteList({
                include_groups: false,
                include_meta_locations: true,
              })
            ),
          loadPage: async ({ search }) => {
            const page = (await LocationsAPI.get({
              include_groups: false,
              include_meta_locations: true,
            })) as GetLocationsResponse;

            return {
              items: page.map((location) => ({
                label: location.location_name,
                value: location.location_id.toString(),
                data: location,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Location Path",
        pluralLabel: "Location Paths",
        dataField: "location_path",
        inputType: "text",
        operators: [
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Size (GB)",
        pluralLabel: "Sizes (GB)",
        dataField: "capacity_gb",
        inputType: "number",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Size (Bytes)",
        pluralLabel: "Sizes (Bytes)",
        dataField: "capacity_bytes",
        inputType: "number",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Parent Evidence Number",
        pluralLabel: "Parent Evidence Numbers",
        dataField: "parent_name",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Received By",
        pluralLabel: "Received By",
        dataField: "received_by",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Received From",
        pluralLabel: "Received From",
        dataField: "received_from",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
      {
        label: "Client",
        pluralLabel: "Clients",
        dataField: "client_id",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        manualSearch: true,
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(ClientsQuery.queryKeys.infiniteList({ search })),
          loadPage: async ({ pageParam, search }) => {
            const page = (await ClientsAPI.get({
              page: pageParam as number,
              search,
            })) as GetClientsResponse;

            return {
              items: page.data.map((client) => ({
                label: client.name,
                value: client.client_id.toString(),
                data: client,
              })),
              nextPageParam: page.nextPage,
            };
          },
        },
      },
      {
        label: "Client Organization",
        pluralLabel: "Client Organizations",
        dataField: "client_organization",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([...OrganizationsQuery.queryKeys.infiniteList()]),
          loadPage: async ({ search }) => {
            const page =
              (await OrganizationsAPI.get()) as GetOrganizationsResponse;

            return {
              items: page.map((org) => ({
                label: org.organization,
                value: org.organization,
                data: org,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Office",
        pluralLabel: "Offices",
        dataField: "office_id",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([...OfficesQuery.queryKeys.infiniteList()]),
          loadPage: async ({ search }) => {
            const page = (await OfficesAPI.get()) as GetOfficesResponse;

            return {
              items: page.map((office) => ({
                label: office.office_name,
                value: office.office_id.toString(),
                data: office,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Linked Contact",
        pluralLabel: "Linked Contacts",
        dataField: "linked_contact_id",
        inputType: "multiselect",
        operators: [
          Operators.In,
          Operators.NIn,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify([
              ...ContactsQuery.queryKeys.infiniteList({ case_id }),
            ]),
          loadPage: async ({ search }) => {
            const page = (await ContactsAPI.get({
              case_id,
            })) as GetContactsResponse;

            return {
              items: page.data.map((contact) => ({
                label: contact.name,
                value: contact.client_id.toString(),
                data: contact,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Description",
        pluralLabel: "Descriptions",
        dataField: "description",
        inputType: "text",
        operators: [
          Operators.Contains,
          Operators.DoesNotContain,
          Operators.IsEmpty,
          Operators.IsNotEmpty,
        ],
      },
    ];

    return filters;
  }, [case_id]);

  const customFieldFilters: FilterDefinition[] = useMemo(() => {
    return customFields?.data?.map(resolveCustomFieldFilter) || [];
  }, [customFields]);

  const filters = [...baseFilters, ...customFieldFilters];

  return filters;
};
